import { ChangeEvent, useState } from 'react';
import * as R from 'ramda';

import { getInstanceInputValidation } from '@atom/selectors/formSelectors';
import {
  CiModuleInspectionUpdate,
  FormAssetErrors,
  FormInstanceFieldItem,
  FormModuleKeys,
  PciModuleDataUpdate,
} from '@atom/types/form';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import { Media } from '@atom/types/media';
import { InventorySchemaItem } from '@atom/types/schema';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { isNumericZero } from '@atom/utilities/validationUtilities';

import InstanceAssetContainer from './assetField/InstanceAssetContainer';
import CiField from './formInstanceModules/ci/CiField';
import FhwaField from './formInstanceModules/FhwaField';
import PciField from './formInstanceModules/pci/PciField';
import PciSummary from './formInstanceModules/pci/PciSummary';
import CurrencyField from './regularFields/CurrencyField';
import DatePickerField from './regularFields/DatePickerField';
import DescriptionField from './regularFields/DescriptionField';
import EnumMultipleField from './regularFields/EnumMultipleField';
import EnumSingleField from './regularFields/EnumSingleField';
import IFrameField from './regularFields/IFrameField';
import LongTextField from './regularFields/LongTextField';
import NumericField from './regularFields/NumericField';
import RadioField from './regularFields/RadioField';
import ShortTextField from './regularFields/ShortTextField';
import TitleField from './regularFields/TitleField';

interface Props {
  field: FormInstanceFieldItem;
  pageName: string;
  isEditable: boolean;
  preview: boolean;
  rootSchema: InventorySchemaItem;
  id: string;
  media: Media[];
  inventoryAssetId: string;
  inventoryAsset?: InventoryAssetDetailType;
  formInstanceId: any;
  uploadFiles: (
    files?: any[] | FileList,
    subjectId?: string,
    subjectType?: string,
    inspectionUpdate?: CiModuleInspectionUpdate,
  ) => void;
  removeFile: (mediaId: string, subjectId: string) => void;
  progressiveUpdateFormInstanceField: (fieldId: string, body: Object) => void;
  progressiveUpdateFormInstanceAttribute: (
    fieldId: string,
    assetId: string,
    attributeGroupName: string,
    attributeId: string,
    body: Object,
  ) => void;
  progressiveUpdateFormInstanceFhwa: (
    fieldId: string,
    assetId: string,
    body: Object,
    elementPath: any[],
  ) => void;
  progressiveUpdateFormInstanceCi: (update: CiModuleInspectionUpdate) => void;
  progressiveUpdateFormInstancePci: (update: PciModuleDataUpdate) => void;
  renameMedia: (mediaId: string, subjectId: string, name: string) => void;
  savingFormInstance: boolean;
  assetErrors?: FormAssetErrors;
}

const FieldBlock = ({
  field,
  pageName,
  removeFile,
  id,
  rootSchema,
  progressiveUpdateFormInstanceAttribute,
  media,
  uploadFiles,
  progressiveUpdateFormInstanceFhwa,
  progressiveUpdateFormInstanceCi,
  progressiveUpdateFormInstancePci,
  progressiveUpdateFormInstanceField,
  inventoryAssetId,
  inventoryAsset,
  isEditable,
  preview,
  formInstanceId,
  renameMedia,
  savingFormInstance,
  assetErrors,
}: Props) => {
  const [value, setValue] = useState(
    isNumericZero(field.type, field.value) || field.value ? field.value : '',
  );

  const progressiveSaveField = fieldValue => {
    const body = { value: fieldValue };
    progressiveUpdateFormInstanceField(id, body);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;

    const isValidated = getInstanceInputValidation(field.type, newValue);

    if (isValidated) {
      if (field.type === 'number' || field.type === 'currency') {
        // @ts-ignore
        newValue = Number(event.target.value);
      }

      progressiveSaveField(newValue);
      setValue(newValue);
    }
  };

  const onDateChange = (dateMillis: number) => {
    const newValue = R.isNil(dateMillis) ? '' : dateMillis;
    progressiveSaveField(newValue);
    setValue(newValue);
  };

  const onSingleSelectChange = (event: any) => {
    progressiveSaveField(event.target.value);
    setValue(event.target.value);
  };

  const onMultiSelectChange = (event: any) => {
    progressiveSaveField(event.target.value);
    setValue(event.target.value);
  };

  const onRadioChange = (newValue: string) => {
    progressiveSaveField(newValue);
    setValue(newValue);
  };

  const fieldFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const subjectType = 'formInstanceField';

    uploadFiles(files, id, subjectType);
  };

  const isInputEditable =
    hasRolePermissions(ROLE_SETS.INSPECTOR) && (isEditable || preview);
  const isInputEditableWhileSaving =
    hasRolePermissions(ROLE_SETS.INSPECTOR) &&
    (isEditable || preview) &&
    !savingFormInstance;
  const isUploadDisabled =
    !hasRolePermissions(ROLE_SETS.INSPECTOR) || preview || savingFormInstance;

  switch (field.type) {
    case 'title': {
      return <TitleField field={field} />;
    }
    case 'description': {
      return <DescriptionField field={field} />;
    }
    case 'shorttext': {
      return (
        <ShortTextField
          fieldFileUpload={fieldFileUpload}
          field={field}
          media={media}
          fieldId={id}
          onChange={onChange}
          value={value}
          removeFile={removeFile}
          isEditable={isInputEditable}
          isUploadDisabled={isUploadDisabled}
          renameMedia={renameMedia}
        />
      );
    }
    case 'longtext': {
      return (
        <LongTextField
          fieldFileUpload={fieldFileUpload}
          field={field}
          media={media}
          fieldId={id}
          onChange={onChange}
          value={value}
          removeFile={removeFile}
          isEditable={isInputEditable}
          isUploadDisabled={isUploadDisabled}
          renameMedia={renameMedia}
        />
      );
    }
    case 'number': {
      return (
        <NumericField
          fieldFileUpload={fieldFileUpload}
          field={field}
          media={media}
          fieldId={id}
          onChange={onChange}
          value={value}
          removeFile={removeFile}
          isEditable={isInputEditable}
          isUploadDisabled={isUploadDisabled}
          renameMedia={renameMedia}
        />
      );
    }
    case 'enumsingle': {
      return (
        <EnumSingleField
          fieldFileUpload={fieldFileUpload}
          field={field}
          media={media}
          fieldId={id}
          onChange={onSingleSelectChange}
          value={value}
          removeFile={removeFile}
          isEditable={isInputEditableWhileSaving}
          isUploadDisabled={isUploadDisabled}
          renameMedia={renameMedia}
        />
      );
    }
    case 'enummultiple': {
      return (
        <EnumMultipleField
          fieldFileUpload={fieldFileUpload}
          field={field}
          media={media}
          fieldId={id}
          onChange={onMultiSelectChange}
          value={value}
          removeFile={removeFile}
          isEditable={isInputEditableWhileSaving}
          isUploadDisabled={isUploadDisabled}
          renameMedia={renameMedia}
        />
      );
    }
    case 'radio': {
      return (
        <RadioField
          fieldFileUpload={fieldFileUpload}
          field={field}
          media={media}
          fieldId={id}
          onChange={onRadioChange}
          value={value}
          removeFile={removeFile}
          isEditable={isInputEditableWhileSaving}
          isUploadDisabled={isUploadDisabled}
          renameMedia={renameMedia}
        />
      );
    }
    case 'currency': {
      return (
        <CurrencyField
          fieldFileUpload={fieldFileUpload}
          field={field}
          media={media}
          fieldId={id}
          onChange={onChange}
          value={value}
          removeFile={removeFile}
          isEditable={isInputEditable}
          isUploadDisabled={isUploadDisabled}
          renameMedia={renameMedia}
        />
      );
    }
    case 'date': {
      return (
        <DatePickerField
          fieldFileUpload={fieldFileUpload}
          field={field}
          media={media}
          fieldId={id}
          onChange={onDateChange}
          value={value}
          removeFile={removeFile}
          isEditable={isInputEditableWhileSaving}
          isUploadDisabled={isUploadDisabled}
          renameMedia={renameMedia}
        />
      );
    }
    case 'asset': {
      return (
        <InstanceAssetContainer
          removeFile={removeFile}
          uploadFiles={uploadFiles}
          isUploadDisabled={isUploadDisabled}
          field={field}
          fieldId={id}
          media={media}
          preview={preview}
          isEditable={isInputEditable}
          rootSchema={rootSchema}
          progressiveUpdateFormInstanceAttribute={
            progressiveUpdateFormInstanceAttribute
          }
          renameMedia={renameMedia}
          savingFormInstance={savingFormInstance}
          assetErrors={assetErrors}
        />
      );
    }
    case FormModuleKeys.FHWA: {
      return (
        // TODO: refactor this tree in its own PR
        <FhwaField
          removeFile={removeFile}
          renameMedia={renameMedia}
          rootSchema={rootSchema}
          formInstanceId={formInstanceId}
          preview={preview}
          uploadFiles={uploadFiles}
          inventoryAssetId={inventoryAssetId}
          field={field}
          fieldId={id}
          media={media}
          isEditable={isInputEditable}
          isUploadDisabled={isUploadDisabled}
          progressiveUpdateFormInstanceFhwa={progressiveUpdateFormInstanceFhwa}
        />
      );
    }
    case FormModuleKeys.CI: {
      return (
        <CiField
          field={field}
          fieldId={id}
          assetId={inventoryAssetId}
          media={media}
          preview={preview}
          onChange={progressiveUpdateFormInstanceCi}
          uploadFiles={uploadFiles}
          removeFile={removeFile}
          renameMedia={renameMedia}
          isEditable={isInputEditable}
        />
      );
    }
    case FormModuleKeys.PCI: {
      return (
        <PciField
          formInstanceId={formInstanceId}
          fieldId={id}
          field={field}
          pageName={pageName}
          inventoryAsset={inventoryAsset}
          inventoryAssetId={inventoryAssetId}
          media={media}
          onChange={progressiveUpdateFormInstancePci}
          preview={preview}
          removeFile={removeFile}
          renameMedia={renameMedia}
          isEditable={isInputEditable}
        />
      );
    }
    case FormModuleKeys.PCI_SUMMARY: {
      return (
        <PciSummary
          fieldId={id}
          field={field}
          inventoryAsset={inventoryAsset}
          inventoryAssetId={inventoryAssetId}
        />
      );
    }
    case 'iframe': {
      return <IFrameField field={field} />;
    }
    default: {
      return <div />;
    }
  }
};

export default FieldBlock;
